import { useCallback, useEffect, useState } from "react";
import { EmailBadge } from "@/components/admin/EmailBadge";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/api";
import { fmtDT } from "@/pages/admin/AdminAccounts";
import { bulkSummary } from "@/pages/admin/AdminData";

const sel = "h-10 rounded-lg border border-slate-200 bg-white px-2 text-sm";

export default function AdminMessages() {
  const [type, setType] = useState("");
  const [status, setStatus] = useState("new");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ rows: [], total: 0, pages: 1, new_count: 0 });
  const [picked, setPicked] = useState([]);
  const load = useCallback(() => {
    adminApi.get("/messages", { params: { type, status, page } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [type, status, page]);
  useEffect(() => { load(); setPicked([]); }, [load]);

  const handle = async (ids) => {
    try {
      const { data: r } = await adminApi.post("/messages/bulk", { action: "handled", ids });
      (r.skipped ? toast.warning : toast.success)(bulkSummary(r, "marked handled"));
      setPicked([]);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const open = data.rows.filter((m) => m.status === "new").map((m) => m.id);
  const allOn = open.length > 0 && open.every((id) => picked.includes(id));

  return (
    <div className="space-y-4" data-testid="admin-messages">
      <div className="flex flex-wrap items-center gap-2">
        <select value={type} onChange={(e) => { setType(e.target.value); setPage(1); }} className={sel} data-testid="messages-filter-type"><option value="">All types</option><option value="sales">Sales</option><option value="support">Support</option></select>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className={sel} data-testid="messages-filter-status"><option value="">All statuses</option><option value="new">New</option><option value="handled">Handled</option></select>
        <label className="ml-2 flex items-center gap-2 text-sm font-semibold"><input type="checkbox" checked={allOn} disabled={!open.length} onChange={() => setPicked(allOn ? [] : open)} className="h-4 w-4 accent-slate-900" data-testid="messages-select-all" />Select all new on this page</label>
        {picked.length > 0 && <button onClick={() => handle(picked)} className="h-9 rounded-lg bg-emerald-600 px-4 text-xs font-bold text-white" data-testid="messages-bulk-handled">Mark {picked.length} handled</button>}
        <span className="ml-auto font-mono text-xs text-slate-500" data-testid="messages-total">{data.total} messages · {data.new_count} new</span>
      </div>
      <div className="space-y-3">
        {data.rows.map((m) => (
          <article key={m.id} className={`rounded-2xl border bg-white p-5 ${m.status === "new" ? "border-amber-300" : "border-slate-200"}`} data-testid={`message-${m.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              {m.status === "new" && <input type="checkbox" checked={picked.includes(m.id)} onChange={() => setPicked((p) => (p.includes(m.id) ? p.filter((x) => x !== m.id) : [...p, m.id]))} className="h-4 w-4 accent-slate-900" data-testid={`message-select-${m.id}`} aria-label="Select message" />}
              <span className={`rounded-full px-2.5 py-0.5 font-mono text-[10.5px] font-bold uppercase ${m.type === "sales" ? "bg-amber-500 text-slate-950" : "bg-slate-900 text-white"}`} data-testid={`message-type-${m.id}`}>{m.type}</span>
              <span className="font-display font-bold text-slate-900">{m.business_name}</span>
              <span className="text-sm text-slate-500">· {m.contact_person}</span>
              <span className="ml-auto"><EmailBadge rec={m} testid={`message-email-status-${m.id}`} /></span>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${m.status === "new" ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`} data-testid={`message-status-${m.id}`}>{m.status === "new" ? "New" : "Handled"}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs text-slate-600">
              <a href={`tel:${m.phone}`} data-testid={`message-phone-${m.id}`}>{m.phone}</a>
              <a href={`mailto:${m.email}`} data-testid={`message-email-${m.id}`}>{m.email}</a>
              <span>{fmtDT(m.created_at)}</span>
            </div>
            <p className="mt-3 whitespace-pre-wrap break-words text-sm text-slate-800" data-testid={`message-body-${m.id}`}>{m.message}</p>
            {m.status === "new" && <button onClick={() => handle([m.id])} className="mt-3 h-9 rounded-lg border-2 border-slate-900 px-4 text-xs font-bold" data-testid={`message-handled-${m.id}`}>Mark handled</button>}
          </article>
        ))}
        {!data.rows.length && <div className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-slate-400" data-testid="messages-empty">No messages</div>}
      </div>
      <div className="flex items-center justify-end gap-2 text-sm">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="h-9 rounded-lg border border-slate-200 bg-white px-3 disabled:opacity-40" data-testid="messages-prev">Prev</button>
        <span className="font-mono text-xs">{page} / {data.pages}</span>
        <button disabled={page >= data.pages} onClick={() => setPage(page + 1)} className="h-9 rounded-lg border border-slate-200 bg-white px-3 disabled:opacity-40" data-testid="messages-next">Next</button>
      </div>
    </div>
  );
}
